import React, { useState } from 'react';
import { API, axios } from '../../utils/api';
import { classi, useInvioUnico } from '../accesso/campi';
import {
  dataLunga, ora, traQuanto, titoloVisita, indirizzoCompleto, linkIndicazioni, linkWhatsApp, leggiDataUtcRoma, quandoGiorno,
  appuntamentoInGiornata, titoloBreve,
} from '../../utils/percorso';
import Icona from './Icone';
import { useTraduzioni } from '../../i18n';
import { FotoConsulente, useScossone } from './SezioniHome';

// Card "Oggi": gli appuntamenti di oggi non ancora passati (visite, proposta, passi della banca, compromesso,
// rogito), una riga ciascuno. Per la visita: Indicazioni, Sposta (richiesta al consulente) e la sua ultima nota.

// Un invio alla volta: tre Invio con la rete lenta mandavano tre richieste (e tre avvisi allo staff).
// onRifiuto(messaggio): il server rifiuta (4xx: visita annullata nel frattempo, ora già passata): la pagina deve
// rileggere i dati, altrimenti la card resta "Visita giovedì 17 · 16:00" con Indicazioni fino a una ricarica.
export const Sposta = ({ visita, consulente, onRichiesta, onRifiuto }) => {
  const { t, errore: tradErrore } = useTraduzioni();
  const [messaggio, setMessaggio] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState(false);
  const { scossa, scuoti, fine } = useScossone();
  const invio = useInvioUnico();
  const wa = linkWhatsApp(consulente.telefono);

  const invia = async (e) => {
    e.preventDefault();
    if (!invio.blocca()) return;
    setAttesa(true);
    setErrore(false);
    try {
      await axios.post(`${API}/visite/${visita.id}/spostamento`, { messaggio: messaggio.trim() });
      onRichiesta(); // si resta bloccati: la richiesta è partita e il modulo si chiude
    } catch (err) {
      invio.sblocca();
      // 4xx: il motivo del server ("Questa visita non si può più spostare"); rete e 5xx: il testo generico
      const stato = err?.response?.status;
      const detail = stato < 500 && err?.response?.data?.detail;
      setErrore(typeof detail === 'string' ? tradErrore(detail) : true);
      scuoti();
      if (onRifiuto && stato >= 400 && stato < 500 && stato !== 401) {
        onRifiuto(typeof detail === 'string' ? `${tradErrore(detail).replace(/\.$/, '')}.` : t('home.sposta.visita_cambiata'));
      }
    } finally {
      setAttesa(false);
    }
  };

  return (
    <form className={classi('sposta', scossa && 'shake')} onAnimationEnd={fine} onSubmit={invia} data-testid="sposta">
      <p>{t('home.sposta.spiegazione', { nome: consulente.nome })}</p>
      {/* segnaposto corto: a 390 px "Es. domenica mattina, o dopo le 18 (facoltativo)" si leggeva "Es. domenica matt" */}
      <div className="note">
        <input value={messaggio} onChange={(e) => setMessaggio(e.target.value)} maxLength={500}
          placeholder={t('home.sposta.segnaposto')} aria-label={t('home.sposta.etichetta')} />
        <button type="submit" className={classi('btn p sm', attesa && 'busy')} aria-busy={attesa} disabled={attesa}>{t('home.sposta.bottone')}</button>
      </div>
      {typeof errore === 'string' && <div className="err" role="alert">{errore.replace(/\.$/, '')}.</div>}
      {errore === true && (
        <div className="err" role="alert">
          {t('home.sposta.errore')}{' '}
          {wa ? <a href={wa} target="_blank" rel="noopener noreferrer"><u>{t('home.sposta.whatsapp', { nome: consulente.nome })}</u></a> : t('home.sposta.riprovate')}
        </div>
      )}
    </form>
  );
};

const RigaVisita = ({ appuntamento, consulente, adesso, onAggiorna, onRifiuto }) => {
  const { t, l } = useTraduzioni();
  const { visita, immobile, data } = appuntamento;
  const [apri, setApri] = useState(false);
  const [inviata, setInviata] = useState(false);
  const richiesta = inviata || !!visita.spostamento_richiesto;
  const agenzia = immobile?.agenzia || visita.immobile_agenzia;
  const dove = indirizzoCompleto(immobile) || [visita.immobile_indirizzo, visita.immobile_citta].filter(Boolean).join(', ');
  const indicazioni = linkIndicazioni(null, dove);
  const dataNota = leggiDataUtcRoma(visita.data_note_prima); // l'ora di Roma, come adesso
  // chi accompagna davvero (visita.con, dal server) e chi ha scritto la nota fissando la visita; il consulente solo se
  // manca (prima sempre "con Sandro" anche quando accompagna Ernesto, come dice il calendario)
  const conChi = visita.con || consulente.nome;
  const fotoNota = visita.con && visita.con !== consulente.nome ? { nome: visita.con } : consulente;

  return (
    <div className="riga" data-testid="riga-oggi">
      <div className="v">
        <div className="t">{ora(data)}<small>{traQuanto(data, adesso, l)}</small></div>
        <div>
          <h2>{titoloVisita(immobile?.titolo || visita.immobile_titolo, l)}</h2>
          <p>{[dove || immobile?.citta, conChi && t('home.oggi.con', { nome: conChi }), agenzia && t('home.oggi.agenzia', { nome: agenzia })].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="acts">
          {indicazioni && <a className="btn sm p" href={indicazioni} target="_blank" rel="noopener noreferrer">{t('comune.indicazioni')}</a>}
          <button type="button" className="btn sm" onClick={() => setApri(!apri)} disabled={richiesta} aria-expanded={apri}>
            {t(richiesta ? 'comune.spostamento_chiesto' : 'comune.sposta')}
          </button>
        </div>
      </div>
      {apri && !richiesta && (
        <Sposta visita={visita} consulente={consulente} onRichiesta={() => { setInviata(true); setApri(false); onAggiorna(); }}
          onRifiuto={(messaggio) => { setApri(false); if (onRifiuto) onRifiuto(messaggio); else onAggiorna(); }} />
      )}
      {richiesta && (
        <p className="sposta"><span className="fatto">{t('home.oggi.richiesta_inviata')}</span> {t('home.oggi.propone_orario', { nome: consulente.nome })}</p>
      )}
      {visita.note_prima && (
        <div className="msg">
          <FotoConsulente consulente={fotoNota} />
          <div>
            <p>“{visita.note_prima}”</p>
            <small>{conChi}{dataNota ? ` · ${t('home.riepilogo.alle_ore', { quando: quandoGiorno(dataNota, adesso, l), ora: ora(dataNota) })}` : ''}</small>
          </div>
        </div>
      )}
    </div>
  );
};

// gli stessi titoli del percorso e del calendario del cliente (testo_per_cliente in backend/agenda.py)
const TITOLI = { rogito: 'rogito', perizia: 'perizia', proposta: 'proposta', delibera: 'delibera', compromesso: 'compromesso' };

// Proposta, passi della banca (perizia, delibera), compromesso o rogito (dal notaio)
const RigaAppuntamento = ({ appuntamento, adesso }) => {
  const { t, l } = useTraduzioni();
  const { tipo, proposta, immobile, data, previsto } = appuntamento;
  const rogito = tipo === 'rogito';
  const luogo = rogito ? proposta.rogito_luogo : indirizzoCompleto(immobile);
  // un rogito solo previsto non è un appuntamento fissato: niente ora e niente "Indicazioni" (la data si conferma
  // dopo la delibera; il calendario e il .ics dicono "Rogito previsto", tutto il giorno)
  const indicazioni = previsto ? null : linkIndicazioni(null, luogo);
  // il titolo senza la città in coda: la città si scrive una volta (prima "Trilocale via Lombardia 5, Monza · Monza")
  const casa = immobile ? titoloBreve(immobile, l) : null;
  const dettagli = rogito
    ? [proposta.rogito_luogo, proposta.rogito_notaio && t('home.oggi.notaio', { nome: proposta.rogito_notaio }), casa]
    : [['perizia', 'delibera'].includes(tipo) && proposta.mutuo_banca && t('home.oggi.banca', { nome: proposta.mutuo_banca }), casa, immobile?.citta];
  return (
    <div className="riga" data-testid="riga-oggi">
      <div className="v">
        {appuntamentoInGiornata(appuntamento)
          ? <div className="t">{t('home.oggi.oggi')}<small>{t('home.oggi.in_giornata')}</small></div>
          : <div className="t">{ora(data)}<small>{traQuanto(data, adesso, l)}</small></div>}
        <div>
          <h2>{t(rogito && previsto ? 'calendario.titoli.rogito_previsto' : `calendario.titoli.${TITOLI[tipo] || 'proposta'}`)}</h2>
          <p>{dettagli.filter(Boolean).join(' · ')}</p>
        </div>
        <div className="acts">
          {indicazioni && <a className="btn sm p" href={indicazioni} target="_blank" rel="noopener noreferrer">{t('comune.indicazioni')}</a>}
        </div>
      </div>
    </div>
  );
};

const CardOggi = ({ appuntamenti, consulente, adesso, onAggiorna, onRifiuto }) => {
  const { t, l } = useTraduzioni();
  return (
    <section className="oggi r" data-testid="card-oggi">
      <div className="k">{t('home.oggi.titolo', { data: dataLunga(adesso, new Date(), l) })}</div>
      {appuntamenti.map((a) => (a.tipo === 'visita'
        ? <RigaVisita key={a.id} appuntamento={a} consulente={consulente} adesso={adesso} onAggiorna={onAggiorna} onRifiuto={onRifiuto} />
        : <RigaAppuntamento key={a.id} appuntamento={a} adesso={adesso} />))}
    </section>
  );
};

export default CardOggi;
